// Montage Event Bus (M2 Sprint)
// Client-side event system for propagating montage changes to all open viewers

import { useRef, useEffect } from "react";
import logger from "@shared/utils/logger";

export interface MontageChangedEvent {
  montage_id: string;
  montage_name: string;
  montage_type: "bipolar" | "referential" | "laplacian";
  reference_type?: string;
  channel_count: number;
  channel_pairs?: any; // Channel pair definitions from montage
  timestamp: number;
  source: "editor" | "perspective" | "quick-switch";
  perspective_id?: string;
}

export interface MontageAppliedEvent extends MontageChangedEvent {
  acknowledged: boolean; // Whether user acknowledged in clinical mode
  previous_montage_id?: string;
  previous_montage_name?: string;
}

export interface MontageRevertedEvent {
  reverted_to_montage_id: string;
  reverted_from_montage_id: string;
  timestamp: number;
}

type MontageEventType =
  | "montage:changed"
  | "montage:applied"
  | "montage:reverted";

type MontageEventData =
  | MontageChangedEvent
  | MontageAppliedEvent
  | MontageRevertedEvent;

type MontageEventListener = (data: MontageEventData) => void;

/**
 * MontageEventBus - Singleton event bus for montage-related events
 *
 * Enables communication between:
 * - MontageEditor → Perspective viewers
 * - QuickSwitch → All open EEG viewers
 * - PerspectiveEditor → Preview panels
 *
 * Usage:
 * ```typescript
 * // Subscribe
 * const unsubscribe = MontageEventBus.on('montage:applied', (event) => {
 *   console.log('Montage applied:', event.montage_name);
 *   updateWaveformView(event.montage_id);
 * });
 *
 * // Publish
 * MontageEventBus.emit('montage:applied', {
 *   montage_id: '123',
 *   montage_name: 'Longitudinal Bipolar',
 *   ...
 * });
 *
 * // Unsubscribe
 * unsubscribe();
 * ```
 */
class MontageEventBusClass {
  private listeners: Map<MontageEventType, Set<MontageEventListener>> =
    new Map();
  private eventHistory: Array<{
    type: MontageEventType;
    data: MontageEventData;
    timestamp: number;
  }> = [];
  private maxHistorySize = 50;

  /**
   * Subscribe to a montage event (alias for 'on')
   * @returns Unsubscribe function
   */
  subscribe(
    event: MontageEventType,
    listener: MontageEventListener,
  ): () => void {
    return this.on(event, listener);
  }

  /**
   * Subscribe to a montage event
   * @returns Unsubscribe function
   */
  on(event: MontageEventType, listener: MontageEventListener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(listener);

    // Return unsubscribe function
    return () => {
      this.off(event, listener);
    };
  }

  /**
   * Unsubscribe from a montage event
   */
  off(event: MontageEventType, listener: MontageEventListener): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(listener);
    }
  }

  /**
   * Emit a montage event to all subscribers
   */
  emit(event: MontageEventType, data: MontageEventData): void {
    const eventListeners = this.listeners.get(event);

    if (eventListeners) {
      eventListeners.forEach((listener) => {
        try {
          listener(data);
        } catch (error) {
          logger.error(`Error in montage event listener for ${event}:`, error);
        }
      });
    }

    // Store in history for debugging
    this.eventHistory.push({
      type: event,
      data,
      timestamp: Date.now(),
    });

    // Trim history if needed
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }
  }

  /**
   * Subscribe to an event once, then automatically unsubscribe
   */
  once(event: MontageEventType, listener: MontageEventListener): () => void {
    const wrappedListener = (data: MontageEventData) => {
      listener(data);
      this.off(event, wrappedListener);
    };

    return this.on(event, wrappedListener);
  }

  /**
   * Clear all listeners for a specific event or all events
   */
  clear(event?: MontageEventType): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get event history for debugging
   */
  getHistory(): Array<{
    type: MontageEventType;
    data: MontageEventData;
    timestamp: number;
  }> {
    return [...this.eventHistory];
  }

  /**
   * Get count of active listeners
   */
  getListenerCount(event?: MontageEventType): number {
    if (event) {
      return this.listeners.get(event)?.size || 0;
    }

    let total = 0;
    this.listeners.forEach((listeners) => {
      total += listeners.size;
    });
    return total;
  }

  /**
   * Check if there are any listeners for an event
   */
  hasListeners(event: MontageEventType): boolean {
    const eventListeners = this.listeners.get(event);
    return eventListeners ? eventListeners.size > 0 : false;
  }
}

// Singleton instance
export const MontageEventBus = new MontageEventBusClass();

// Convenience hook for React components with proper cleanup
export const useMontageEvents = () => {
  const subscriptions = useRef<Array<() => void>>([]);

  useEffect(() => {
    return () => {
      subscriptions.current.forEach((unsub) => unsub());
      subscriptions.current = [];
    };
  }, []);

  return {
    subscribe: (
      event: MontageEventType,
      handler: (data: MontageEventData) => void,
    ) => {
      MontageEventBus.on(event, handler);
      const unsub = () => MontageEventBus.off(event, handler);
      subscriptions.current.push(unsub);
      return unsub;
    },
    publish: MontageEventBus.emit.bind(MontageEventBus),
  };
};

// Type guards
export const isMontageAppliedEvent = (
  data: MontageEventData,
): data is MontageAppliedEvent => {
  return "acknowledged" in data;
};

export const isMontageRevertedEvent = (
  data: MontageEventData,
): data is MontageRevertedEvent => {
  return "reverted_to_montage_id" in data && "reverted_from_montage_id" in data;
};

export default MontageEventBus;
